/**
 * EmailApp — root Preact component for the full CSR email UI.
 *
 * Renders the full 3-pane layout (sidebar | list | reading pane) entirely
 * client-side from JSON API data. Mounted into #email-app-root by email_controller.js.
 *
 * When store.isThreadOnlyView is true (popup window via ?view=thread), only the
 * reading pane is shown — no sidebar, folder rail, or email list.
 *
 * Props:
 *   store     {object}  — emailStore instance
 *   mailboxes {array}   — bootstrapped mailbox data from data attr
 *   i18n      {object}  — i18n-js instance
 */

import { h, createContext } from 'preact'
import { useContext } from 'preact/hooks'
import { EmailSidebar } from './EmailSidebar.jsx'
import { EmailList } from './EmailList.jsx'
import { EmailThread } from './EmailThread.jsx'
import { EmailCompose } from './EmailCompose.jsx'
import { EmailRulesPanel } from './EmailRulesPanel.jsx'
import { TaskCreateFromEmailModal } from './TaskCreateFromEmailModal.jsx'
import { TaskLinkPicker } from './TaskLinkPicker.jsx'

export const EmailStoreContext = createContext(null)

export function useEmailStore() {
  return useContext(EmailStoreContext)
}

export function EmailApp({ store, i18n, logoUrl = '' }) {
  const mailboxes = store.mailboxes.value
  const threadOnly = store.isThreadOnlyView.value

  // Task create / link modals are mounted permanently (Bootstrap needs a stable
  // element to attach to) and toggle themselves off the store signals.
  const taskModals = (
    <>
      <TaskCreateFromEmailModal store={store} i18n={i18n} />
      <TaskLinkPicker store={store} i18n={i18n} />
    </>
  )

  if (threadOnly) {
    return (
      <EmailStoreContext.Provider value={store}>
        <div class="email-layout email-layout--thread-only">
          <div class="email-reading-pane-wrapper">
            {store.isComposing.value
              ? <EmailCompose store={store} i18n={i18n} mailboxes={mailboxes} />
              : <EmailThread store={store} i18n={i18n} />
            }
          </div>
        </div>
        {taskModals}
      </EmailStoreContext.Provider>
    )
  }

  return (
    <EmailStoreContext.Provider value={store}>
      <div class="email-layout">

        {/* Sidebar */}
        <div class="email-sidebar">
          <EmailSidebar store={store} i18n={i18n} logoUrl={logoUrl} />
        </div>

        {store.isRulesOpen.value ? (
          <div class="email-rules-pane">
            <EmailRulesPanel store={store} i18n={i18n} />
          </div>
        ) : (
          <>
            {/* List pane */}
            <div class="email-list-pane" data-email-list-pane>
              <EmailList store={store} i18n={i18n} />
            </div>

            {/* Resizer handle */}
            <div class="email-resizer" data-email-resizer />

            {/* Reading pane */}
            <div class="email-reading-pane-wrapper">
              {store.isComposing.value
                ? <EmailCompose store={store} i18n={i18n} mailboxes={mailboxes} />
                : <EmailThread store={store} i18n={i18n} />
              }
            </div>
          </>
        )}

      </div>
      {taskModals}
    </EmailStoreContext.Provider>
  )
}
